<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Roles</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Title</th>
                        <th>Portal</th>
                        <th>Role Type</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="role in roles" v-if="roles.length" :key="role.id" class="text-left">
                        <!-- The numeric id is what permissions.role_id, company_users.role_id
                             and user_sessions.role_id all key on, so surface it here rather
                             than making anyone look it up in the database. -->
                        <td class="text-medium-emphasis">{{ role.id }}</td>
                        <td>{{ role.title }}</td>
                        <td class="text-capitalize">{{ role.name }}</td>
                        <td class="text-capitalize">{{ role.role_type }}</td>
                        <td>
                            <!-- Derived circle tiers have no actions at all. The API
                                 refuses all four writes with a 403, so offering the
                                 buttons only produces an error toast. is_derived_tier
                                 comes from the server: the rule is title AND personal
                                 portal, and matching the four titles here would flag a
                                 commercial role that happens to share one. -->
                            <v-chip v-if="role.is_derived_tier" color="info"
                                    size="small" variant="tonal">
                                Derived
                                <v-tooltip activator="parent" location="top" max-width="320">
                                    Circle roles are derived from ownership, the circle
                                    admin flag and date of birth, and are kept in step by
                                    a nightly sync. They cannot be edited by hand.
                                </v-tooltip>
                            </v-chip>
                            <v-btn-group v-else density="compact" divided variant="outlined">
                                <v-btn
                                    v-if="role.role_type !== 'api'"
                                    :disabled="menuGroupDialogSwitch" class="text-primary"
                                    size="x-small" @click="roleMenuGroups(role)">Menu Groups
                                </v-btn>
                                <v-btn :disabled="permissionsDialogSwitch" class="text-black"
                                       size="x-small" @click="rolePermissions(role)">Permissions
                                </v-btn>
                                <template v-if="!role.is_default">
                                    <v-btn :disabled="editDialogSwitch" class="text-warning"
                                           size="x-small" @click="editRole(role)">Edit
                                    </v-btn>
                                    <v-btn class="text-error" size="x-small"
                                           @click="deleteRole(role)">Delete
                                    </v-btn>
                                </template>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="5">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="5">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
    </v-container>

    <Create-Role v-if="createDialogSwitch" v-model="createDialogSwitch" @addRole="addRole"/>
    <Edit-Role v-if="editDialogSwitch" v-model="editDialogSwitch" :role="selectedRole" @updateRole="updateRole"/>
    <Permissions v-if="permissionsDialogSwitch" v-model="permissionsDialogSwitch" :role="selectedRole"/>
    <menu-groups v-if="menuGroupDialogSwitch" v-model="menuGroupDialogSwitch" :role="selectedRole"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import EditRole from "@/components/dialogs/roles/Edit.vue";
import CreateRole from "@/components/dialogs/roles/Create.vue";
import MenuGroups from "@/components/dialogs/roles/MenuGroups.vue";
import Permissions from "@/components/dialogs/roles/Permissions.vue";

const roles = ref([]);
const loading = ref(true);
const {showSnackbar} = useSnackbarStore();
const permissionsDialogSwitch = ref(false);
const menuGroupDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const editDialogSwitch = ref(false);
const selectedRole = ref(null);

onMounted(() => {
    fetchRoles();
});

const fetchRoles = () => {
    send('get', `admin/roles`)
        .then((response) => {
            roles.value = response.data.data;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Roles could not be loaded', 'error')
        })
}
const addRole = (role) => {
    roles.value.unshift(role);
}
const editRole = (role) => {
    selectedRole.value = role;
    editDialogSwitch.value = true;
}
const updateRole = (role) => {
    roles.value.map((comp, index) => {
        if (comp.id === role.id) {
            roles.value[index] = role;
        }
    });
}
const rolePermissions = (role) => {
    selectedRole.value = role;
    permissionsDialogSwitch.value = true;
}
const roleMenuGroups = (role) => {
    selectedRole.value = role;
    menuGroupDialogSwitch.value = true;
}
const deleteRole = (role) => {
    if (confirm(`Are you sure to delete "${role.title}" role?`)) {
        send('delete', `admin/roles/${role.id}`)
            .then((response) => {
                if (response.data.success) {
                    roles.value.map((item, index) => {
                        if (item.id === role.id) {
                            roles.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${role.title}" Role could not be deleted`, 'error')
                }
            })
    }
}
</script>
